
import { supabase } from "@/integrations/supabase/client";
import { Interview } from "@/types/interview";
import { InterviewMapperService } from "../base/interviewMapperService";

/**
 * Service for managing interviews in the feeder queue
 */
export class FeederInterviewsService {
  /**
   * Fetch all interviews marked for the feeder queue
   */
  static async getFeederInterviews(): Promise<Interview[]> {
    console.log('Fetching feeder interviews');
    
    try {
      // Explicit check for BOTH is_in_feeder=true AND publication_status='pending'
      // This ensures published interviews are not included even if is_in_feeder wasn't updated
      const { data, error } = await supabase
        .from('interviews')
        .select('*')
        .eq('is_in_feeder', true as any)
        .eq('publication_status', 'pending' as any) // Only get pending interviews in the feeder
        .order('timestamp', { ascending: false });

      if (error) {
        console.error('Error fetching feeder interviews:', error);
        throw new Error(`Failed to fetch feeder interviews: ${error.message}`);
      }

      console.log(`Found ${data?.length || 0} feeder interviews`);
      
      // Log all feeder interviews for debugging
      if (data && data.length > 0) {
        console.log('Feeder interviews details:');
        data.forEach((interview: any) => {
          console.log(`ID: ${interview.id}, Status: ${interview.publication_status}, In Feeder: ${interview.is_in_feeder}`);
        });
      }
      
      return data ? data.map(interview => InterviewMapperService.mapDatabaseInterviewToModel(interview as any)) : [];
    } catch (error) {
      console.error('Error in getFeederInterviews:', error);
      throw error;
    }
  }

  /**
   * Mark an interview as part of the feeder queue
   */
  static async markForFeeder(interviewId: string): Promise<boolean> {
    console.log('Marking interview for feeder:', interviewId);
    
    try {
      // First check if the interview exists and get its current state
      const { data: existingInterview, error: checkError } = await supabase
        .from('interviews')
        .select('id, is_in_feeder')
        .eq('id', interviewId as any)
        .maybeSingle();
      
      if (checkError) {
        console.error('Error checking interview:', checkError);
        throw new Error(`Interview check failed: ${checkError.message}`);
      }
      
      if (!existingInterview) {
        console.error('Interview not found for ID:', interviewId);
        throw new Error('Interview not found');
      }
      
      console.log('Current interview state:', existingInterview);
      
      // If it's already in the feeder, just return success
      if ((existingInterview as any)?.is_in_feeder === true) {
        console.log('Interview is already marked for feeder, returning success');
        return true;
      }
      
      // Attempt to update the is_in_feeder flag using Supabase's RPC to ensure transaction safety
      // Use a type assertion to handle the RPC function name
      const { data: result, error: updateError } = await supabase.rpc(
        'mark_interview_for_feeder' as any, 
        { interview_id: interviewId }
      );
      
      if (updateError) {
        console.error('RPC Error marking for feeder:', updateError);
        throw new Error(`Failed to mark interview for feeder: ${updateError.message}`);
      }
      
      if (result === true) {
        console.log('Successfully marked interview for feeder via RPC function');
        return true;
      } else {
        console.error('RPC function returned false, update was not successful');
        return false;
      }
    } catch (error) {
      console.error('Critical error in markForFeeder:', error);
      throw error; // Re-throw for proper error handling upstream
    }
  }
}
